import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('onor_token'));
  const [loading, setLoading] = useState(true);

  // F1: si el backend responde 401 por sesión cerrada (otro equipo entró,
  // "cerrar en todos mis equipos", admin) o token caducado, se sale de la app
  // con un aviso en la pantalla de acceso.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        const detail = error?.response?.data?.detail;
        const code = detail && typeof detail === 'object' ? detail.code : null;
        const forced = ['session_replaced', 'session_revoked', 'token_expired'];
        if (error?.response?.status === 401 && forced.includes(code) && localStorage.getItem('onor_token')) {
          try { sessionStorage.setItem('onor_logout_reason', detail.message || 'Tu sesión se cerró. Vuelve a iniciar sesión.'); } catch (e) { /* sin sessionStorage */ }
          localStorage.removeItem('onor_token');
          delete axios.defaults.headers.common['Authorization'];
          setToken(null);
          setUser(null);
          setUserStatus(null);
          if (!window.location.pathname.startsWith('/login')) window.location.assign('/login');
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  // Set axios default header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Fetch user status for routing decisions
  const fetchUserStatus = useCallback(async () => {
    if (!token) return null;
    try {
      const response = await axios.get(`${API}/users/me/status`);
      setUserStatus(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user status:', error);
      return null;
    }
  }, [token]);

  // El estado de ruteo cambia desde el panel admin (pedir cuestionario,
  // reiniciar plan, aprobar). Se vuelve a consultar al volver a la pestaña,
  // al recuperar el foco y cada minuto, para que el usuario no tenga que
  // recargar la página para ver el cambio.
  useEffect(() => {
    if (!token) return undefined;
    const onVisible = () => { if (document.visibilityState === 'visible') fetchUserStatus(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', fetchUserStatus);
    const timer = setInterval(fetchUserStatus, 60000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', fetchUserStatus);
      clearInterval(timer);
    };
  }, [token, fetchUserStatus]);

  // Load user on mount
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/users/me`);
          setUser(response.data);
          await fetchUserStatus();
        } catch (error) {
          console.error('Failed to load user:', error);
          localStorage.removeItem('onor_token');
          setToken(null);
          setUser(null);
          setUserStatus(null);
        }
      }
      setLoading(false);
    };

    loadUser();
  }, [token, fetchUserStatus]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('onor_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    // Fetch status after login
    const statusRes = await axios.get(`${API}/users/me/status`);
    setUserStatus(statusRes.data);
    
    return { user: userData, status: statusRes.data };
  };

  const register = async (email, password, name, phone, acceptLegal = false) => {
    const response = await axios.post(`${API}/auth/register`, { 
      email, 
      password, 
      name,
      phone,
      accept_legal: acceptLegal === true,
    });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('onor_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    // New users need onboarding
    setUserStatus({
      routing: {
        needs_onboarding: true,
        needs_assessment: false,
        needs_approval: false,
        can_access_app: false
      }
    });
    
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('onor_token');
    setToken(null);
    setUser(null);
    setUserStatus(null);
  };

  const updateOnboarding = async (mode) => {
    const response = await axios.patch(`${API}/users/me/onboarding`, {
      mode_selected: mode
    });
    
    // Refresh user and status
    const userResponse = await axios.get(`${API}/users/me`);
    setUser(userResponse.data);
    
    const statusRes = await axios.get(`${API}/users/me/status`);
    setUserStatus(statusRes.data);
    
    return response.data;
  };

  const updateProfile = async (data) => {
    const response = await axios.patch(`${API}/users/me`, data);
    setUser(response.data);
    return response.data;
  };

  const refreshStatus = async () => {
    const status = await fetchUserStatus();
    // Also refresh user data
    try {
      const userRes = await axios.get(`${API}/users/me`);
      setUser(userRes.data);
    } catch (e) {
      console.error('Error refreshing user:', e);
    }
    return status;
  };

  // Recarga solo el usuario (sin tocar el status de ruteo). Lo usan las
  // pantallas de perfil tras subir foto/portada para que el avatar del navbar
  // se actualice sin recargar la página.
  const refreshUser = async () => {
    try {
      const userRes = await axios.get(`${API}/users/me`);
      setUser(userRes.data);
      return userRes.data;
    } catch (e) {
      console.error('Error refreshing user:', e);
      return null;
    }
  };

  const hasRole = (roles) => {
    if (!user?.roles) return false;
    if (typeof roles === 'string') {
      return user.roles.includes(roles);
    }
    return roles.some(role => user.roles.includes(role));
  };

  const isCoach = () => hasRole(['coach', 'coach_basic', 'coach_pro', 'coach_elite']);
  const isAdmin = () => hasRole(['admin', 'super_admin']);

  // Routing helpers based on userStatus
  const needsOnboarding = () => userStatus?.routing?.needs_onboarding ?? false;
  const needsAssessment = () => userStatus?.routing?.needs_assessment ?? false;
  const needsApproval = () => userStatus?.routing?.needs_approval ?? false;
  const canAccessApp = () => userStatus?.routing?.can_access_app ?? false;
  const getPlanStatus = () => userStatus?.plan?.status ?? null;
  const getAssessmentStatus = () => userStatus?.assessment?.status ?? null;
  // Estado del plan que debe ver el usuario, por tipo ('workout' | 'nutrition'):
  // 'active' | 'updating' (tiene plan y se prepara uno nuevo) | 'in_progress'
  // (aún no tiene plan) | 'none'.
  const getPlanView = (kind = 'workout') => {
    const view = userStatus?.plan?.views?.[kind];
    if (view) return view;
    const status = userStatus?.assessment?.status;
    return ['plans_generating', 'plans_pending_review', 'plans_failed'].includes(status)
      ? 'in_progress'
      : 'none';
  };
  
  // Subscription helpers
  const isSuspended = () => user?.subscription_status === 'suspended' || user?.blocked_reason === 'subscription_expired';
  const getBlockedReason = () => user?.blocked_reason ?? null;

  const value = {
    user,
    userStatus,
    token,
    loading,
    login,
    register,
    logout,
    updateOnboarding,
    updateProfile,
    refreshStatus,
    refreshUser,
    hasRole,
    isCoach,
    isAdmin,
    // Routing helpers
    needsOnboarding,
    needsAssessment,
    needsApproval,
    canAccessApp,
    getPlanStatus,
    getAssessmentStatus,
    getPlanView,
    // Subscription helpers
    isSuspended,
    getBlockedReason,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
